interface SelectOption {
  value: string;
  text: string;
  reverse: string;
  slug: string;
  aliases: string[];
}

interface SearchSelectWrapper extends HTMLElement {
  _refresh?: () => void;
}

export function upgradeToSearchSelect(select: HTMLSelectElement): SearchSelectWrapper {
  if (select.dataset.upgraded) return select.nextElementSibling as SearchSelectWrapper;
  select.dataset.upgraded = '1';

  const wrapper = document.createElement('div') as SearchSelectWrapper;
  wrapper.className = 'search-select';

  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'search-select__input';
  input.placeholder = select.options[0]?.text || 'search...';
  input.autocomplete = 'off';

  const dropdown = document.createElement('div');
  dropdown.className = 'search-select__dropdown';

  wrapper.appendChild(input);
  wrapper.appendChild(dropdown);

  select.style.display = 'none';
  select.parentNode!.insertBefore(wrapper, select.nextSibling);

  function getOptions(): SelectOption[] {
    const opts: SelectOption[] = [];
    for (const opt of select.options) {
      if (!opt.value) continue;
      const aliasStr = opt.dataset.aliases || '';
      opts.push({
        value: opt.value,
        text: opt.text,
        reverse: opt.dataset.reverse || '',
        slug: opt.dataset.slug || '',
        aliases: aliasStr ? aliasStr.split('|') : [],
      });
    }
    return opts;
  }

  let focusedIndex = -1;

  function getVisibleOptions(): NodeListOf<HTMLElement> {
    return dropdown.querySelectorAll('.search-select__option');
  }

  function setFocusedIndex(idx: number) {
    const items = getVisibleOptions();
    for (const item of items) {
      item.classList.remove('search-select__option--focused');
    }
    focusedIndex = idx;
    if (idx >= 0 && idx < items.length) {
      items[idx].classList.add('search-select__option--focused');
      items[idx].scrollIntoView({ block: 'nearest' });
    }
  }

  function matchedAlias(aliases: string[], query: string): string | null {
    if (!query) return null;
    for (const a of aliases) {
      if (a.toLowerCase().includes(query)) return a;
    }
    return null;
  }

  function render(filter?: string) {
    const options = getOptions();
    const query = (filter || '').toLowerCase();
    const filtered = query
      ? options
          .filter(o =>
            o.text.toLowerCase().includes(query) ||
            o.slug.toLowerCase().includes(query) ||
            o.aliases.some(a => a.toLowerCase().includes(query))
          )
          .sort((a, b) => {
            const aText = a.text.toLowerCase(), bText = b.text.toLowerCase();
            const aSlug = a.slug.toLowerCase(), bSlug = b.slug.toLowerCase();
            const aExact = aText === query || aSlug === query;
            const bExact = bText === query || bSlug === query;
            if (aExact !== bExact) return aExact ? -1 : 1;
            const aStarts = aText.startsWith(query) || aSlug.startsWith(query);
            const bStarts = bText.startsWith(query) || bSlug.startsWith(query);
            if (aStarts !== bStarts) return aStarts ? -1 : 1;
            // Alias matches rank after direct name/slug matches
            const aAlias = a.aliases.some(al => al.toLowerCase().includes(query));
            const bAlias = b.aliases.some(al => al.toLowerCase().includes(query));
            if (aAlias !== bAlias) return aAlias ? 1 : -1;
            return 0;
          })
      : options;

    focusedIndex = -1;

    if (filtered.length === 0) {
      dropdown.innerHTML = '<div class="search-select__empty">no matches</div>';
    } else {
      dropdown.innerHTML = filtered.map(o => {
        const alias = query ? matchedAlias(o.aliases, query) : null;
        const nameMatches = !query || o.text.toLowerCase().includes(query) || o.slug.toLowerCase().includes(query);
        const aliasHtml = alias && !nameMatches
          ? `<span class="search-select__alias">alias: ${alias}</span>`
          : '';
        return `<div class="search-select__option${o.value === select.value ? ' search-select__option--selected' : ''}" data-value="${o.value}" data-reverse="${o.reverse}">${o.text}${o.slug ? `<span class="search-select__slug">${o.slug}</span>` : ''}${aliasHtml}</div>`;
      }).join('');
    }
  }

  function open() {
    render(input.value);
    dropdown.style.display = 'block';
  }

  function close() {
    dropdown.style.display = 'none';
  }

  function selectOption(value: string, text: string, reverse: string) {
    select.value = value;
    const opt = [...select.options].find(o => o.value === value && (o.dataset.reverse || '') === reverse);
    if (opt) opt.selected = true;
    input.value = text;
    close();
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  input.addEventListener('focus', open);
  input.addEventListener('input', open);

  dropdown.addEventListener('mousedown', (e) => {
    const opt = (e.target as HTMLElement).closest('.search-select__option') as HTMLElement | null;
    if (opt) {
      e.preventDefault();
      selectOption(opt.dataset.value!, opt.childNodes[0].textContent!, opt.dataset.reverse || '');
    }
  });

  input.addEventListener('blur', () => {
    setTimeout(close, 150);
  });

  input.addEventListener('keydown', (e) => {
    const items = getVisibleOptions();
    const itemCount = items.length;

    if (e.key === 'Escape') {
      input.value = '';
      select.value = '';
      close();
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (dropdown.style.display !== 'block') open();
      if (itemCount > 0) {
        setFocusedIndex(focusedIndex < itemCount - 1 ? focusedIndex + 1 : 0);
      }
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (dropdown.style.display !== 'block') open();
      if (itemCount > 0) {
        setFocusedIndex(focusedIndex > 0 ? focusedIndex - 1 : itemCount - 1);
      }
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const target = (focusedIndex >= 0 && focusedIndex < itemCount)
        ? items[focusedIndex]
        : items[0];
      if (target) {
        selectOption(target.dataset.value!, target.childNodes[0].textContent!, target.dataset.reverse || '');
      }
      return;
    }
  });

  const form = select.closest('form');
  if (form) {
    form.addEventListener('reset', () => {
      setTimeout(() => { input.value = ''; }, 0);
    });
  }

  if (select.value) {
    const opt = [...select.options].find(o => o.value === select.value);
    if (opt) input.value = opt.text;
  }

  wrapper._refresh = () => {
    if (dropdown.style.display === 'block') {
      render(input.value);
    }
    if (select.value) {
      const opt = [...select.options].find(o => o.value === select.value);
      if (opt) input.value = opt.text;
    }
  };

  return wrapper;
}
